import { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useAuth } from "@/hooks/useAuth";
import { isSupabaseConfigured, supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { useNavigate, Link } from "react-router-dom";

const AuthRegisterForm = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { signUp } = useAuth();

  if (!isSupabaseConfigured) {
    return (
      <div className="text-center py-8">
        <h2 className="text-2xl font-heading font-bold mb-4">Registration Unavailable</h2>
        <p className="text-muted-foreground">Supabase authentication is not configured. Please check your environment variables.</p>
      </div>
    );
  }

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const form = new FormData(e.currentTarget);
      const email = String(form.get("email") || "").trim();
      const password = String(form.get("password") || "");
      const username = String(form.get("username") || "").trim().toLowerCase();
      const firstName = String(form.get("firstName") || "").trim();
      const lastName = String(form.get("lastName") || "").trim();
      const age = parseInt(String(form.get("age") || "0"));
      const phone = String(form.get("phone") || "").trim();

      // Basic validation
      if (!email || !password || !username || !firstName || !lastName || !age) {
        toast.error("Please fill in all required fields");
        return;
      }

      if (age < 13 || age > 120) {
        toast.error("Age must be between 13 and 120 years");
        return;
      }

      if (username.length < 3) {
        toast.error("Username must be at least 3 characters long");
        return;
      }

      if (!/^[a-zA-Z0-9_-]+$/.test(username)) {
        toast.error("Username can only contain letters, numbers, hyphens, and underscores");
        return;
      }

      console.log("Attempting customer registration for:", email);

      // Check if username is already taken
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('username')
        .eq('username', username)
        .maybeSingle();

      if (existingProfile) {
        toast.error("Username is already taken. Please choose a different one.");
        return;
      }

      // Register with extended metadata
      const userMetadata = {
        role: 'buyer',
        username,
        first_name: firstName,
        last_name: lastName,
        age,
        phone: phone || null
      };

      const { data, error } = await signUp(email, password, userMetadata);

      if (error) {
        console.error("Registration error:", error);

        // Provide user-friendly error messages
        let userMessage = "Registration failed";
        if (error.message.includes("User already registered")) {
          userMessage = "An account with this email already exists. Please sign in instead.";
        } else if (error.message.includes("Password should be at least")) {
          userMessage = "Password must be at least 6 characters long.";
        } else if (error.message.includes("Invalid email")) {
          userMessage = "Please enter a valid email address.";
        } else if (error.message.includes("Signup is disabled")) {
          userMessage = "New registrations are currently disabled. Please contact support.";
        } else {
          userMessage = error.message || "Registration failed";
        }

        toast.error(userMessage);
        return;
      }

      console.log("Registration successful:", data);

      // Create user profile with the additional information
      if (data.user) {
        try {
          const { error: profileError } = await supabase
            .from('user_profiles')
            .insert({
              user_id: data.user.id,
              username,
              first_name: firstName,
              last_name: lastName,
              age,
              phone: phone || null,
              full_name: `${firstName} ${lastName}`,
            });

          if (profileError) {
            console.error("Profile creation error:", profileError);
            // Don't fail registration if profile creation fails, just log it
            toast.warning("Account created, but profile setup needs completion. Please update your profile after signing in.");
          }
        } catch (profileErr) {
          console.error("Profile creation failed:", profileErr);
        }
      }
      if (data.user && !data.user.email_confirmed_at) {
        toast.success(`Welcome ${firstName}! Please check your email and click the verification link to complete registration.`);
        setTimeout(() => {
          navigate("/login");
        }, 3000);
      } else if (data.user && data.user.email_confirmed_at) {
        toast.success(`Welcome to Bahama Marketplace, ${firstName}! Your account is ready.`);
        navigate("/products");
      } else {
        toast.success(`Account created for ${firstName}! You can now sign in.`);
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }

    } catch (err: any) {
      console.error("Unexpected registration error:", err);
      toast.error("An unexpected error occurred during registration");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div>
        <h2 className="text-2xl font-heading font-bold mb-2">Create your account</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Join Bahama Marketplace and discover amazing products from local sellers.
        </p>
      </div>

      {/* Enhanced Registration Form */}
      <form className="flex flex-col gap-4 w-full" onSubmit={onSubmit}>

        {/* Personal Information */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="firstName">First Name *</Label>
            <Input 
              required 
              id="firstName" 
              name="firstName" 
              type="text" 
              placeholder="John" 
              className="mt-1" 
              disabled={loading}
            />
          </div>
          <div>
            <Label htmlFor="lastName">Last Name *</Label>
            <Input 
              required 
              id="lastName" 
              name="lastName" 
              type="text" 
              placeholder="Doe" 
              className="mt-1" 
              disabled={loading}
            />
          </div>
        </div>

        {/* Username */}
        <div>
          <Label htmlFor="username">Username *</Label>
          <Input 
            required 
            id="username" 
            name="username" 
            type="text" 
            placeholder="johndoe123" 
            className="mt-1" 
            disabled={loading}
            minLength={3}
            maxLength={30}
          />
          <p className="text-xs text-muted-foreground mt-1">
            3-30 characters, letters, numbers, hyphens and underscores only
          </p>
        </div>

        {/* Age */}
        <div>
          <Label htmlFor="age">Age *</Label>
          <Input 
            required 
            id="age" 
            name="age" 
            type="number" 
            placeholder="25" 
            className="mt-1" 
            disabled={loading}
            min={13}
            max={120}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Must be at least 13 years old
          </p>
        </div>

        {/* Contact Information */}
        <div>
          <Label htmlFor="email">Email Address *</Label>
          <Input 
            required 
            id="email" 
            name="email" 
            type="email" 
            placeholder="john@example.com" 
            className="mt-1" 
            disabled={loading}
          />
        </div>

        <div>
          <Label htmlFor="phone">Phone Number (Optional)</Label>
          <Input 
            id="phone" 
            name="phone" 
            type="tel" 
            placeholder="+1 (555) 123-4567" 
            className="mt-1" 
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Optional - for order updates and account security
          </p>
        </div>

        {/* Password */}
        <div>
          <Label htmlFor="password">Password *</Label>
          <Input 
            required 
            id="password" 
            name="password" 
            minLength={6} 
            type="password" 
            placeholder="••••••••" 
            className="mt-1" 
            disabled={loading}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Must be at least 6 characters
          </p>
        </div>
        <Button 
          size="lg" 
          className="mt-4" 
          type="submit" 
          disabled={loading}
        >
          {loading ? "Creating Account..." : "Create Account"}
        </Button>

        <div className="text-center space-y-2">
          <button 
            type="button" 
            className="text-sm text-muted-foreground hover:text-foreground underline" 
            onClick={() => navigate("/login")}
            disabled={loading}
          >
            Already have an account? Sign in
          </button>

          <div className="pt-2 border-t border-border/40">
            <p className="text-xs text-muted-foreground mb-2">
              Want to sell on Bahama Marketplace?
            </p>
            <Link 
              to="/seller/register" 
              className="text-sm text-primary hover:underline font-medium"
            >
              Become a seller →
            </Link>
          </div>
        </div>
      </form>
    </div>
  );
};
export default AuthRegisterForm;